<script setup lang="ts" generic="T">
import Multiselect from "vue-multiselect";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { debounce } from "perfect-debounce";
import { DebounceEnum } from "~/utils/enums/debounce.enums";
import { isNoResponseError } from "~/utils/helpers/httpErrors";

const isLoading = ref(false);
const searchQuery = ref("");
const multiselectRef = ref<HTMLElement | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
const selectRef = ref(null);

const emit = defineEmits(["update:modelValue", "clickAdd", "searchValue", "open", "loadMore"]);
const offset = ref(0); // to track the offset for lazy loading

// Concurrency guards for asyncFind. Two distinct problems they solve:
//  • open() re-fired asyncFind while the onMounted fetch was still in flight; `offset` only
//    advances after the await, so both calls asked for offset=0 and the first page was appended
//    twice. `pending` lets open() skip that redundant fetch.
//  • the reset/push below happen AFTER the await, so a slow reply landing late overwrote a newer
//    one — typing a search then watching the unfiltered first page come back. `requestSeq` makes
//    any superseded reply a no-op.
const pending = ref(0);
let requestSeq = 0;
// From a keystroke until that search's first page has landed, debounce wait included.
const searchResultsPending = ref(false);

const localOptions = ref<any[]>([]);

const props = defineProps({
  options: {
    type: [Array<T>],
    default() {
      return [];
    },
  },
  dependencyKey: {
    type: null,
    default: undefined,
  },
  store: {
    type: Object,
    default: null,
  },
  storeObject: {
    type: String,
    default: null,
  },
  modelValue: {
    type: null,
  },
  trackBy: {
    type: String,
    default: null,
  },
  label: {
    type: String,
    default: null,
  },
  limiter: {
    type: Number,
    default: null,
  },
  add: {
    type: Boolean,
    default: false,
  },
  alwaysShowAdd: {
    type: Boolean,
    default: false,
  },
  // Tab validates a typed search too: picks the option named exactly like it, or adds it. Opt-in,
  // because most add actions replace the current selection, which a Tab out of the field must not.
  addOnTab: {
    type: Boolean,
    default: false,
  },
  lazyLoad: {
    type: Boolean,
    default: false,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: "Choisissez une option",
  },
  showNoOptions: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  showNoResults: {
    type: Boolean,
    default: true,
  },
  openFetch: {
    type: Boolean,
    default: false,
  },
  delete: {
    type: Boolean,
    default: true,
  },
  hideSelected: {
    type: Boolean,
    default: false,
  },
  nb_Element: {
    type: Number,
    default: 5,
  },
  servicesAPI: {
    type: Function,
  },
  internalSearch: {
    type: Boolean,
    default: true,
  },
  /**
   * The caller is fetching the options itself — grouped lists page from the parent. Shows the same
   * spinner as an internal fetch. Not named `loading`: a good many usages already pass a dead
   * `:loading` binding, and honouring them all at once would light up spinners across the app.
   */
  busy: {
    type: Boolean,
    default: false,
  },
  suggestions: {
    type: Array<any>,
    default: () => [],
  },
  // Grouped options: `options` becomes `[{ [groupLabel]: "…", [groupValues]: [...] }]` and each
  // group gets a non-selectable header row. Left null so every existing usage stays flat.
  groupValues: {
    type: String,
    default: null,
  },
  groupLabel: {
    type: String,
    default: null,
  },
});

/** Whether this dropdown fetches its own options, or just renders the static `options` it was given. */
const hasAsyncFetch = computed(() => !!props.servicesAPI);

interface QueryParams {
  [key: string]: any;
}
// Watcher pour réinitialiser les options locales quand les suggestions changent
// TODO: Test this watcher on preprod
// watch(() => props.suggestions, (newSuggestions) => {
//     if (newSuggestions?.length) {
//         localOptions.value = [...newSuggestions];
//     }
// }, { deep: true, immediate: true });

onMounted(() => {
  // Initialiser les options locales avec les suggestions
  if (props.suggestions?.length) {
    localOptions.value = [...props.suggestions];
  }

  if (hasAsyncFetch.value) {
    // Initial page only. The scroll listener is attached in open() — the multiselect content
    // wrapper doesn't exist in the DOM until the dropdown opens (so it's null here).
    asyncFind("", true);
  }
});

watch(
  () => props.dependencyKey,
  (newVal, oldVal) => {
    if (newVal === oldVal) return;
    offset.value = 0;
    if (hasAsyncFetch.value) asyncFind(searchQuery.value, true);
  },
);

async function asyncFind(query: string, init?: boolean) {
  getSearchQuery(query);
  // Imperative fetch — do NOT wrap in useAsyncData. Since Nuxt 4 it caches by key and replays
  // the cached result without re-running the handler; even the previous dynamic key froze
  // infinite-scroll pagination (offset was not part of the key). servicesAPI (SDK) is already
  // fresh on every call.
  const seq = ++requestSeq;
  pending.value += 1;
  if (!init) isLoading.value = true;
  const queryParams: QueryParams = {
    limit: 10,
    offset: offset.value,
    ...{ search: query },
  };

  let api: any;
  try {
    api = await props.servicesAPI?.(queryParams);
  } catch (error) {
    if (!init) isLoading.value = false;
    // No answer came back: nothing to fix in the app, and the next scroll or search asks again.
    if (isNoResponseError(error)) return;
    throw error;
  } finally {
    pending.value -= 1;
  }

  // A newer request started while this one was in flight, so its result is the current truth and
  // applying this one would overwrite it. Drop the reply and leave `offset` alone — the newer
  // call owns pagination now.
  if (seq !== requestSeq) {
    if (!init) isLoading.value = false;
    return;
  }

  /** SDK */
  // `api` can legitimately be undefined here: a `servicesAPI` that skips (e.g. readonly) resolves
  // to nothing. Guard `api` itself — the `?.` after `.data` only protected `.value` onward, so
  // `api.data` still threw when `api` was undefined.
  const data = api?.data?.value?.data?.length ? api.data.value.data : api?.data;
  // Reset policy: for server-side search (internal-search=false) a new query
  // truly invalidates the previous list, so reset even when the response is
  // empty. For client-side filtering, keep the previous list on an empty
  // response — empty usually means "server ignored search", and the client
  // filter still needs the full list to work on.
  const shouldReset = init && (!props.internalSearch || data?.length > 0);
  if (shouldReset) {
    if (props.suggestions?.length) {
      localOptions.value = [...props.suggestions];
      if (localOptions.value.length > 0) {
        localOptions.value[localOptions.value.length - 1]._beforeSeparator = true;
      }
    } else if (props.store && props.storeObject) {
      props.store[props.storeObject] = [];
    }
  }
  if (data?.length) {
    if (props.suggestions?.length) {
      localOptions.value.push(...data);
    } else {
      props.store[props.storeObject] = props.store[props.storeObject] || [];
      props.store[props.storeObject].push(...data);
    }
  }
  offset.value += 10;
  if (!init) isLoading.value = false;
}

function updateValue(newValue: any) {
  // Prevent accidental deselection when clicking on the already selected item
  if (newValue === null && props.modelValue !== null && props.modelValue !== undefined) {
    return;
  }
  // A group header still reaches `selectGroup` on mousedown even with `group-select` off, and that
  // emits the whole group as an array. This dropdown is always single-select, so it never is one.
  if (Array.isArray(newValue)) {
    return;
  }
  emit("update:modelValue", newValue);
}

function clearAll() {
  emit("update:modelValue", null);
}

function getSearchQuery(query: string) {
  searchQuery.value = query;
  emit("searchValue", query);
}
const debouncedAsyncFind = debounce((q: string) => {
  offset.value = 0;
  asyncFind(q, true)
    .then(() => {
      nextTick(() => {
        if (scrollContainer.value) {
          scrollContainer.value.scrollTop = 0; // Reset scroll position
        }
      });
    })
    .finally(() => {
      if (q === searchQuery.value) searchResultsPending.value = false;
    });
}, DebounceEnum.HIGH);

function searchChange(q: string) {
  // Always sync searchQuery so the parent's `@search-value` listener fires and
  // UI-dependent state (add button visibility, emptyMultiSelect class) stays
  // consistent with what vue-multiselect is showing.
  getSearchQuery(q);

  if (hasAsyncFetch.value) {
    searchResultsPending.value = true;
    debouncedAsyncFind(q);
  }
}

function clickAdd() {
  emit("clickAdd", searchQuery.value);
  // @ts-ignore
  selectRef.value.deactivate();
}

/**
 * Keyboard path to the add action once a search is typed: Enter adds only when nothing is listed, and
 * selects otherwise; with `addOnTab`, Tab picks the listed option named exactly like the search, or
 * adds the search, then moves on to the next field. Until the search's own results land, both keys
 * are held: the list still shows the previous search, so acting on it would pick a stale option or
 * add what the reply lists.
 * Listened in the capture phase because vue-multiselect stops both keys before they bubble here, and
 * a handled key is stopped in turn: once the add clears the search, the multiselect's own Enter would
 * select the first option of the unfiltered list over it.
 */
function onKeydown(event: KeyboardEvent) {
  const query = searchQuery.value.trim().toLowerCase();
  const multiselect = selectRef.value as any;
  const shownOptions: any[] | undefined = multiselect?.filteredOptions;
  if (!props.add || !query.length || !multiselect?.isOpen || !Array.isArray(shownOptions)) return;

  const isTab = props.addOnTab && event.key === "Tab" && !event.shiftKey;
  const isEnter = event.key === "Enter" && !event.isComposing;
  if (!isTab && !isEnter) return;

  if (searchResultsPending.value) {
    event.stopPropagation();
    event.preventDefault();
    return;
  }
  const isEnterOnEmptyList = isEnter && shownOptions.length === 0;
  if (!isTab && !isEnterOnEmptyList) return;

  event.stopPropagation();
  // Tab keeps its default, which is what moves focus on; Enter must not submit an enclosing form.
  if (isEnterOnEmptyList) event.preventDefault();

  const normalizedLabel = (option: any) =>
    String(multiselect.getOptionLabel(option) ?? "")
      .trim()
      .toLowerCase();
  const exactMatch = shownOptions.find((option) => !option?.$isLabel && normalizedLabel(option) === query);
  if (exactMatch) {
    updateValue(exactMatch);
    multiselect.deactivate();
  } else {
    clickAdd();
  }
}
/**
 * Next page of options: fetched here when this dropdown owns the call, asked of the parent
 * otherwise. Grouped options are assembled by the caller, which is the only one that can say which
 * group a page belongs to; a static list has no listener and nothing happens.
 */
function requestMore() {
  if (hasAsyncFetch.value) {
    asyncFind(searchQuery.value);

    return;
  }

  emit("loadMore", searchQuery.value);
}

function handleScroll() {
  if (!scrollContainer.value || isLoading.value || props.busy) return;

  const { scrollTop, clientHeight, scrollHeight } = scrollContainer.value;
  // Load more results when scrolled to bottom.
  if (scrollTop + clientHeight >= scrollHeight - 10) requestMore();
}

function open() {
  nextTick(() => {
    // vue-multiselect only mounts .multiselect__content-wrapper while the dropdown is open, so it
    // isn't in the DOM at onMounted. (Re)acquire it and attach the infinite-scroll listener on each
    // open — otherwise handleScroll never fires and lazy-loading past the first page never happens.
    const wrapper = multiselectRef.value?.querySelector(".multiselect__content-wrapper") as HTMLElement | null;
    if (wrapper && wrapper !== scrollContainer.value) {
      scrollContainer.value?.removeEventListener("scroll", handleScroll);
      scrollContainer.value = wrapper;
      wrapper.addEventListener("scroll", handleScroll);
    }
    // A list that doesn't overflow can never be scrolled to its end, so its next page is asked here.
    if (
      scrollContainer.value &&
      scrollContainer.value.scrollHeight <= scrollContainer.value.clientHeight &&
      !isLoading.value &&
      pending.value === 0 &&
      !props.busy
    ) {
      requestMore();
    }
  });
  if (props.openFetch) {
    emit("open");
  }
}
</script>
<template>
  <div
    ref="multiselectRef"
    :class="{ 'simple-dropdown--add': props.add }"
    :aria-busy="searchResultsPending"
    @keydown.capture="onKeydown"
  >
    <multiselect
      ref="selectRef"
      :model-value="props.modelValue"
      :options="props.suggestions?.length ? localOptions : props.options"
      :group-values="props.groupValues"
      :group-label="props.groupLabel"
      :group-select="false"
      :loading="isLoading || props.busy"
      :placeholder="placeholder"
      :track-by="trackBy"
      :label="label"
      :max-height="40 * props.nb_Element"
      :show-labels="false"
      :show-pointer="false"
      open-direction="bottom"
      :searchable="searchable"
      :show-no-options="showNoOptions"
      :show-no-results="showNoResults"
      :hide-selected="props.hideSelected"
      :internal-search="internalSearch"
      :class="{
        emptyMultiSelect:
          !(props.suggestions?.length ? localOptions : props.options).length &&
          !searchQuery.length &&
          !(props.add && props.alwaysShowAdd),
        error: props.error,
      }"
      :disabled="disabled"
      @update:modelValue="updateValue"
      @search-change="searchChange"
      @open="open"
    >
      <template v-if="props.modelValue && props.delete && !disabled" #clear="props">
        <div class="multiselect__clear" @mousedown.prevent.stop="clearAll()">
          <SvgIcon class="text-secondary-color hover:text-primary-blue-color" name="close"></SvgIcon>
        </div>
      </template>
      <template
        v-if="((props.suggestions?.length ? localOptions : props.options).length || searchQuery.length) && !disabled"
        #caret="{ toggle, isOpen }"
      >
        <span class="multiselect__select">
          <SvgIcon class="w-full h-full" name="arrow_down" @mousedown.prevent.stop="toggle"></SvgIcon>
        </span>
      </template>
      <template v-if="props.add" #afterList>
        <div
          v-if="add && (alwaysShowAdd || searchQuery.length)"
          class="dropdown__padding sticky bottom-0 flex gap-[8px] items-center cursor-pointer text-primary-blue-color bg-light-grey-color border-t border-stroke-color"
          @click="clickAdd"
        >
          <SvgIcon name="plus"></SvgIcon>
          <span>
            <slot name="add">Ajouter "{{ searchQuery }}"</slot>
          </span>
        </div>
      </template>

      <template #option="{ option, index }">
        <slot name="option" :option="option" :index="index" @scroll="handleScroll"> </slot>
      </template>
      <template #singleLabel="{ option }">
        <slot name="singleLabel" :option="option"></slot>
      </template>
      <template #noResult>
        <span>Aucun résultat</span>
      </template>
    </multiselect>
  </div>
</template>

<!-- Add Multiselect CSS. Can be added as a static asset or inside a component. -->
<!-- <style src="vue-multiselect/dist/vue-multiselect.css"></style> -->
<style lang="scss" scoped>
@use "assets/scss/dropdowns/multiselect";

// The sticky add row pins to its nearest clipping ancestor, and the list's global `overflow: hidden`
// would be that ancestor — it never scrolls, so the row would scroll away with the options instead of
// staying at the bottom of the scrolling wrapper.
.simple-dropdown--add :deep(.multiselect__content) {
  overflow: visible;
}
</style>
